/**
 * orderPlatforms.jsx — v147.16 (Feb 2026)
 *
 * Branded "Order on Swiggy" / "Order on Zomato" / "Order on Ownly"
 * (v149.67) full-width CTA blocks for restaurant bio pages and action
 * pages.
 *
 * Phase 1: deep-link to the platform app (swiggy://, zomato://) with a
 * clean https fallback for browsers where the app scheme isn't
 * registered. Clicks are tracked via the standard ActionBlockRenderer
 * `onClick(block, href)` prop so Page Results already breaks them out
 * per-block-type (`swiggy_order` / `zomato_order`).
 *
 * Phase 2 will replace the click-out with a deep in-page ordering flow
 * against the Swiggy Builders Club MCP (menu → cart → UPI → track)
 * once Linkola's redirect URI is approved. That upgrade will be
 * gated by the same `block_swiggy_order_enabled` feature flag so a
 * fast rollback stays trivial.
 *
 * Rendering rules:
 *   - Block hides entirely when the URL is blank / not https (public).
 *   - Editors see a placeholder card so they know it's there but
 *     unpublished.
 *   - Global feature flag `block_swiggy_order_enabled` /
 *     `block_zomato_order_enabled` hides even a fully-configured
 *     block on the public renderer (checked in ActionBlockRenderer).
 */
import { ExternalLink } from "lucide-react";

// Official-shape single-color Swiggy mark. The public brand mark is a
// stylised lowercase "s" — this SVG is a clean, license-safe rendition
// in the exact orange Swiggy uses, so we don't pull image assets.
function SwiggyIcon({ className, style }) {
  return (
    <svg
      viewBox="0 0 32 32"
      width="26"
      height="26"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      style={style}
    >
      {/* Stylised "s" — approximates the Swiggy wordmark's leading glyph */}
      <path d="M22.6 10.2c-1-1.8-3-3-5.3-3.2-.4 0-.7-.1-1.1-.1-3.7 0-6.6 2.4-6.6 5.6 0 3 2.4 4.6 5.4 5.7l1.4.5c2.3.9 3.6 1.6 3.6 3.3 0 1.6-1.5 2.6-3.7 2.6-2 0-3.4-.8-4.2-2.2-.3-.5-1-.7-1.5-.4l-1.9 1c-.6.3-.8 1-.5 1.6 1.3 2.4 4 3.8 7.9 3.8 4.7 0 7.9-2.5 7.9-6.4 0-3.6-2.7-5.1-5.9-6.3l-1.6-.6c-1.9-.7-3-1.3-3-2.6 0-1.3 1.3-2.1 3.2-2.1 1.6 0 2.7.6 3.3 1.7.3.5 1 .7 1.5.4l1.7-.9c.5-.3.7-.9.4-1.4Z" />
    </svg>
  );
}

// Zomato "Z" mark — clean, license-safe silhouette. Rendered in the
// exact red Zomato uses in their public brand kit.
function ZomatoIcon({ className, style }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="26"
      height="26"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      style={style}
    >
      {/* Rounded-tail Z */}
      <path d="M4.2 4h13.4c1.53 0 2.42 1.72 1.53 2.97l-9.7 13.6c-.4.57.02 1.36.72 1.36h4.3a1 1 0 1 1 0 2H4.15c-1.53 0-2.42-1.72-1.53-2.97l9.7-13.6c.41-.57-.01-1.36-.71-1.36h-4.4l-.02-.01c-1.65 0-3-1.35-3-3v-.01c0-.55.45-1 1-1H4.2Z" />
    </svg>
  );
}

// Ownly (Rapido's food delivery app) mark — two raised strokes over a
// looping smile. A clean, license-safe rendition drawn in Ownly's pink
// (`theme-color` on ownly.food), like the Swiggy / Zomato marks above.
function OwnlyIcon({ className, style }) {
  return (
    <svg
      viewBox="0 0 32 32"
      width="28"
      height="28"
      fill="none"
      aria-hidden="true"
      className={className}
      style={style}
    >
      <g fill="currentColor">
        <ellipse cx="10.6" cy="9.8" rx="2.9" ry="7.9" transform="rotate(-17 10.6 9.8)" />
        <ellipse cx="17.4" cy="8.3" rx="3.1" ry="8.2" />
      </g>
      <g stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.6 12.5C25.4 11.7 29.3 12.6 29.2 14.9C29.1 17.8 23 20.8 15.8 22C9.8 23 4.4 22.6 3.9 21.2C3.5 20 4.7 18 7.2 16.1" />
        <path d="M3.4 21.4C4.4 26.6 9 30.6 16.2 30.6C21.4 30.6 26.4 27.9 29 23.6" />
      </g>
    </svg>
  );
}

// Try opening the platform app; if the app isn't installed the browser
// falls back to the https URL after ~800ms with no visible glitch. Only
// runs on the public renderer — editors get preventDefault via onClick.
function launchPlatform(appScheme, httpsUrl) {
  if (typeof window === "undefined") return;
  // No app scheme (Ownly): the https link is the restaurant's shop link,
  // which the platform hands to its own app — just open it.
  if (!appScheme) {
    window.open(httpsUrl, "_blank", "noopener,noreferrer");
    return;
  }
  // Native Capacitor / Cordova apps and desktop browsers → open web.
  const ua = navigator.userAgent || "";
  const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
  if (!isMobile) {
    window.open(httpsUrl, "_blank", "noopener,noreferrer");
    return;
  }
  const started = Date.now();
  const fallback = window.setTimeout(() => {
    if (document.visibilityState === "visible" && Date.now() - started >= 700) {
      window.open(httpsUrl, "_blank", "noopener,noreferrer");
    }
  }, 800);
  // If the app opens, the browser tab goes hidden → clear the fallback.
  const onVis = () => {
    if (document.visibilityState === "hidden") {
      window.clearTimeout(fallback);
      document.removeEventListener("visibilitychange", onVis);
    }
  };
  document.addEventListener("visibilitychange", onVis);
  // Fire the app-scheme intent. On failure the browser silently ignores it.
  try {
    window.location.href = appScheme;
  } catch {
    window.clearTimeout(fallback);
    window.open(httpsUrl, "_blank", "noopener,noreferrer");
  }
}

function BrandedOrderBlock({
  block,
  onClick,
  editable,
  brand,        // { name, tagline, appScheme (null = open the link), accent, testidType, Icon }
}) {
  const c = block.config || {};
  const enabled = c.enabled !== false;
  const rawUrl = (c.url || "").trim();
  const isHttps = /^https:\/\/\S+/i.test(rawUrl);
  const shown = enabled && isHttps;
  const headline = c.headline || `Order on ${brand.name}`;
  const subtext = c.subtext || brand.tagline;

  // Public visitor with no URL / disabled block: render nothing.
  if (!shown && !editable) return null;

  const handleClick = (e) => {
    if (editable) { onClick?.(e, rawUrl || "#"); e.preventDefault(); return; }
    // Track the click via the standard bio/action-page block-click hook.
    // The renderer's onClick prop already POSTs to /public-page/... with
    // block_id + block_type, which surfaces per-type totals in Page
    // Results ("Swiggy Order link · N clicks").
    onClick?.(e, rawUrl);
    e.preventDefault();
    launchPlatform(brand.appScheme, rawUrl);
  };

  return (
    <a
      href={rawUrl || "#"}
      target={editable ? undefined : "_blank"}
      rel="noopener noreferrer"
      onClick={handleClick}
      className="flex items-center gap-3 rounded-2xl transition-transform hover:-translate-y-[1px]"
      style={{
        background: "#FFFFFF",
        border: `1px solid ${brand.accent}22`,
        boxShadow: `0 6px 20px ${brand.accent}1A, 0 1px 3px rgba(0,0,0,0.05)`,
        minHeight: 76,
        padding: "12px 16px",
        color: "#161616",
      }}
      data-testid={`block-${brand.testidType}-${block.block_id}`}
    >
      <span
        className="inline-flex items-center justify-center flex-shrink-0 rounded-xl"
        style={{
          width: 48,
          height: 48,
          background: `${brand.accent}12`,
          color: brand.accent,
        }}
        aria-hidden="true"
      >
        <brand.Icon />
      </span>
      <span className="flex-1 min-w-0 text-center">
        <span className="block font-display font-black text-[15px] leading-tight tracking-tight">
          {shown ? headline : "URL not set"}
        </span>
        <span className="block text-[12px] mt-0.5" style={{ color: "#545B66" }}>
          {shown ? subtext : "Add a valid https:// link in the editor"}
        </span>
      </span>
      <span
        className="inline-flex items-center justify-center flex-shrink-0 rounded-full"
        style={{
          width: 36,
          height: 36,
          background: brand.accent,
          color: "#FFFFFF",
        }}
        aria-hidden="true"
      >
        <ExternalLink className="w-4 h-4" strokeWidth={2.4} />
      </span>
    </a>
  );
}

const SWIGGY_BRAND = {
  name: "Swiggy",
  tagline: "Get it delivered to your doorstep",
  accent: "#FC8019",
  appScheme: "swiggy://",
  testidType: "swiggy-order",
  Icon: SwiggyIcon,
};

const ZOMATO_BRAND = {
  name: "Zomato",
  tagline: "Order your favourites online",
  accent: "#E23744",
  appScheme: "zomato://",
  testidType: "zomato-order",
  Icon: ZomatoIcon,
};

// v149.67 — Ownly (zero-commission delivery by Rapido). Restaurants paste
// their Ownly shop link (https://ownly.food/app/brand/…); it opens as is.
const OWNLY_BRAND = {
  name: "Ownly",
  tagline: "Order online for doorstep delivery",
  accent: "#FF297D",
  appScheme: null,
  testidType: "ownly-order",
  Icon: OwnlyIcon,
};

export function SwiggyOrderBlock(props) {
  return <BrandedOrderBlock {...props} brand={SWIGGY_BRAND} />;
}

export function ZomatoOrderBlock(props) {
  return <BrandedOrderBlock {...props} brand={ZOMATO_BRAND} />;
}

export function OwnlyOrderBlock(props) {
  return <BrandedOrderBlock {...props} brand={OWNLY_BRAND} />;
}
